import {FC, ReactNode} from 'react'
import {useDispatch} from 'react-redux'
import {gql} from '@apollo/client'
import {range, keyBy, omit} from 'lodash'
import classNames from 'classnames'
import moment from 'moment'
import {
  Control,
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
  UseFormRegister,
  useFormState,
  useWatch,
} from 'react-hook-form'
import {yup} from 'libs/yup'
import {yupResolver} from '@hookform/resolvers/yup'
import Button from 'jbc-front/components/Button'
import ActionButton from 'jbc-front/components/ActionButton'
import {Radio, RadioContainer} from 'jbc-front/components/presenters/form/Radio'
import {Label} from 'components/forms/Label'
import {InputWithText} from 'jbc-front/components/presenters/form/InputWithText'
import {FormErrors} from 'components/reactHookForm/FormErrors'
import {useQuery, useMutation} from 'hooks/graphql'
import {EmployeeName} from 'pages/withAuth/adminPage/procedures/parts/EmployeeName'
import {ProcedureContext, ReportFormProps} from './ReportForm'
import {Panel, Body, Head} from 'components/ui/Panel'
import {dateFormat} from 'libs/formatter'
import styles from './R508Report.scss'
import {DateField} from 'components/reactHookForm/DateField'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import {Note} from 'jbc-front/components/presenters/ui/Note'
import {Textarea} from 'jbc-front/components/presenters/form/Textarea'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {laborConsultantSchema, LaborConsultantSection} from './LaborConsultant'
import {useConfirmUpgrade} from './useConfirmUpgrade'
import {asyncError} from 'store/actions/asyncError'
import {Delete, PlusSquare} from 'jbc-front/components/icons'

// @ts-ignore
import {titleMap} from 'procedureStatuses/Show'

export const APPLICATION_TMP_DATA_NAME = 'procedure_childcare'
const PARENTAL_LEAVE = 'parental_leave'
const MAX_LEAVE_DATA_COUNT = 4

const isParentalLeave = (procedureType: string) => procedureType === PARENTAL_LEAVE

const R508_REPORT = gql`
  query R508Report($procedureStatusId: ID!, $isAdmin: Boolean!, $loadApplication: Boolean!) {
    client {
      id
      procedureStatus(procedureStatusId: $procedureStatusId) {
        id
        procedureType
        employee {
          id
          staffCode
          displayFullName
          myNumberPresent
        }
        procedureChildcareDatum {
          id
          employeeDependent {
            id
          }
          startedOn
          expectedFinishedOn
          isPapaMamaLeavePlus
          leaveDays
          workingDays
          extendedExpectedFinishedOn
          extendedLeaveDays
          finishedOn
          finalizedLeaveDays
          procedureChildcareChildDatum {
            id
            firstName
            lastName
            firstNameKana
            lastNameKana
            birthDate
            relation
            parentingStartedDate
            addSupport
          }
          procedureChildcareLeaveData {
            id
            ordinal
            startedOn
            expectedFinishedOn
            leaveDays
            workingDays
          }
        }
        r508ParamSet {
          id
          notes
          version
          createdAt
          needUpgrade
        }
        r508ParamSetForAdmin: r508ParamSet @include(if: $isAdmin) {
          id
          isUseMyNumber
          submitOn
          needLaborConsultant
          laborConsultantCreateDate
          laborConsultantName
          laborConsultantAgentType
          laborConsultantPrefecture {
            id
          }
          laborConsultantPhoneNumber0
          laborConsultantPhoneNumber1
          laborConsultantPhoneNumber2
        }
        application: procedureTmpDatum(type: application, name: "${APPLICATION_TMP_DATA_NAME}") @include(if: $loadApplication) {
          id
          type
          name
          data
        }
      }
    }
  }
`

interface R508ReportQueryVariables {
  procedureStatusId: string
  isAdmin: boolean
  loadApplication: boolean
}

interface R508ReportQueryResult {
  client: {
    id: string
    procedureStatus: {
      id: string
      procedureType: string
      employee: {
        id: string
        staffCode: string
        displayFullName: string
        myNumberPresent: boolean
      }
      procedureChildcareDatum?: {
        id: string
        employeeDependent?: {
          id: string
        }
        startedOn: string
        expectedFinishedOn?: string
        isPapaMamaLeavePlus: boolean
        leaveDays?: number
        workingDays?: number
        extendedExpectedFinishedOn?: string
        extendedLeaveDays?: number
        finishedOn?: string
        finalizedLeaveDays?: number
        procedureChildcareChildDatum?: {
          id: string
          firstName?: string
          lastName?: string
          firstNameKana?: string
          lastNameKana?: string
          birthDate?: string
          relation: 'real' | 'other'
          parentingStartedDate?: string
          addSupport: boolean
        }
        procedureChildcareLeaveData: {
          id: string
          ordinal: number
          startedOn?: string
          expectedFinishedOn?: string
          leaveDays?: number
          workingDays?: number
        }[]
      }
      r508ParamSet: {
        id?: string
        notes?: string
        version: number
        createdAt?: string
        needUpgrade: boolean
      }
      r508ParamSetForAdmin?: {
        id?: string
        isUseMyNumber: boolean
        submitOn?: string
        needLaborConsultant: boolean
        laborConsultantCreateDate?: string
        laborConsultantName?: string
        laborConsultantAgentType?: 'submission_agent' | 'office_agent'
        laborConsultantPrefecture?: {
          id: string
        }
        laborConsultantPhoneNumber0?: string
        laborConsultantPhoneNumber1?: string
        laborConsultantPhoneNumber2?: string
      }
      application?: {
        id: string
        type: string
        name: string
        data: unknown
      }
    }
  }
}

const UPDATE_PROCEDURE_START_CHILDCARE_DATUM = gql`
  mutation UpdateProcedureChildcareDatum($input: UpdateProcedureChildcareDatumInput!) {
    updateProcedureChildcareDatum(input: $input) {
      procedureChildcareDatum {
        id
      }
    }
  }
`

interface UpdateProcedureChildcareDatumQueryVariables {
  input: {
    procedureStatusId: string
    attributes: {
      isPapaMamaLeavePlus?: boolean | null
      leaveDays?: number | null
      workingDays?: number | null
      extendedExpectedFinishedOn?: string | null
      extendedLeaveDays?: number | null
      finishedOn?: string | null
      finalizedLeaveDays?: number | null
      procedureChildcareLeaveData: {
        ordinal: number
        startedOn?: string | null
        expectedFinishedOn?: string | null
        leaveDays?: number | null
        workingDays?: number | null
      }[]
    }
  }
}

const UPDATE_R508_PARAM_SET = gql`
  mutation UpdateR508ParamSet($input: UpdateR508ParamSetInput!) {
    updateR508ParamSet(input: $input) {
      r508ParamSet {
        id
      }
    }
  }
`

interface UpdateR508ParamSetQueryVariables {
  input: {
    procedureStatusId: string
    attributes: {
      isUseMyNumber?: boolean | null
      submitOn?: string | null
      notes?: string | null
      needLaborConsultant?: boolean | null
      laborConsultantCreateDate?: string | null
      laborConsultantName?: string | null
      laborConsultantAgentType?: 'submission_agent' | 'office_agent' | null
      laborConsultantPrefectureId?: string | null
      laborConsultantPhoneNumber0?: string | null
      laborConsultantPhoneNumber1?: string | null
      laborConsultantPhoneNumber2?: string | null
      version?: number | null
    }
  }
}

const UPDATE_APPLICATION_TMP_DATA = gql`
  mutation UpdateApplicationTmpData($input: UpdateProcedureTmpDatumInput!) {
    updateProcedureTmpDatum(input: $input) {
      procedureStatus {
        id
      }
    }
  }
`

interface UpdateApplicationTmpDataQueryVariables {
  input: {
    procedureStatusId: string
    type: 'application'
    name: typeof APPLICATION_TMP_DATA_NAME
    data: Schema
  }
}

const UPDATE_PROCEDURE_STATUS = gql`
  mutation updateProcedureStatus($input: UpdateProcedureStatusInput!) {
    updateProcedureStatus(input: $input) {
      procedureStatus {
        id
        dateOn
      }
    }
  }
`

interface UpdateProcedureStatusQueryVariables {
  input: {
    id: string
    dateOn?: string | null
    initialized?: boolean
  }
}

export const R508Report: FC<ReportFormProps> = ({
  procedureStatusId,
  submitText,
  afterSubmit,
  procedureContext,
  isConfirm,
  comments,
  renderReject,
}) => {
  const dispatch = useDispatch()
  const {data, loading} = useQuery<R508ReportQueryResult, R508ReportQueryVariables>(R508_REPORT, {
    variables: {
      procedureStatusId,
      isAdmin: procedureContext === 'admin',
      loadApplication: isConfirm || procedureContext === 'mypage',
    },
    fetchPolicy: 'network-only',
  })
  const procedureStatus = data?.client?.procedureStatus

  const [updateProcedureChildcareDatum] = useMutation<unknown, UpdateProcedureChildcareDatumQueryVariables>(
    UPDATE_PROCEDURE_START_CHILDCARE_DATUM
  )
  const [updateR508ParamSet] = useMutation<unknown, UpdateR508ParamSetQueryVariables>(UPDATE_R508_PARAM_SET)
  const [updateApplicationTmpData] = useMutation<unknown, UpdateApplicationTmpDataQueryVariables>(
    UPDATE_APPLICATION_TMP_DATA
  )
  const [updateProcedureStatus] = useMutation<unknown, UpdateProcedureStatusQueryVariables>(UPDATE_PROCEDURE_STATUS)

  if (loading || !procedureStatus) {
    return null
  }

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const submitData = normalizeSubmitData(data)
    const blankToNull = (value: string | undefined | null) => (value ? value : null)

    try {
      switch (procedureContext) {
        case 'admin':
          await updateProcedureChildcareDatum({
            variables: {
              input: {
                procedureStatusId,
                // 共通項目（子情報・開始日・終了予定日等）は ChildcareInfoForm が保存するため、
                // R508 は帳票固有項目のみ送信する（未送信の共通項目はサーバー側で温存される）
                attributes: {
                  isPapaMamaLeavePlus: submitData.childcareDatum.isPapaMamaLeavePlus,
                  leaveDays: submitData.childcareDatum.leaveDays ? Number(submitData.childcareDatum.leaveDays) : null,
                  workingDays: submitData.childcareDatum.workingDays
                    ? Number(submitData.childcareDatum.workingDays)
                    : null,
                  extendedExpectedFinishedOn: blankToNull(submitData.childcareDatum.extendedExpectedFinishedOn),
                  extendedLeaveDays: submitData.childcareDatum.extendedLeaveDays
                    ? Number(submitData.childcareDatum.extendedLeaveDays)
                    : null,
                  finishedOn: blankToNull(submitData.childcareDatum.finishedOn),
                  finalizedLeaveDays: submitData.childcareDatum.finalizedLeaveDays
                    ? Number(submitData.childcareDatum.finalizedLeaveDays)
                    : null,
                  procedureChildcareLeaveData: submitData.childcareLeaveData.map((leave, index) => ({
                    ordinal: index,
                    startedOn: blankToNull(leave.startedOn),
                    expectedFinishedOn: blankToNull(leave.expectedFinishedOn),
                    leaveDays: leave.leaveDays ? Number(leave.leaveDays) : null,
                    workingDays: leave.workingDays ? Number(leave.workingDays) : null,
                  })),
                },
              },
            },
          })
          await updateR508ParamSet({
            variables: {
              input: {
                procedureStatusId,
                attributes: {
                  notes: submitData.r508ParamSet.notes,
                  version: procedureStatus.r508ParamSet.version,
                  ...(submitData.r508ParamSetForAdmin
                    ? {
                        isUseMyNumber: submitData.r508ParamSetForAdmin.isUseMyNumber === 'true',
                        submitOn: blankToNull(submitData.r508ParamSetForAdmin.submitOn),
                        needLaborConsultant: !!submitData.r508ParamSetForAdmin.needLaborConsultant,
                        laborConsultantCreateDate: blankToNull(
                          submitData.r508ParamSetForAdmin.laborConsultantCreateDate
                        ),
                        laborConsultantName: submitData.r508ParamSetForAdmin.laborConsultantName,
                        laborConsultantAgentType: submitData.r508ParamSetForAdmin.laborConsultantAgentType,
                        laborConsultantPrefectureId: submitData.r508ParamSetForAdmin.laborConsultantPrefectureId,
                        laborConsultantPhoneNumber0: submitData.r508ParamSetForAdmin.laborConsultantPhoneNumber0,
                        laborConsultantPhoneNumber1: submitData.r508ParamSetForAdmin.laborConsultantPhoneNumber1,
                        laborConsultantPhoneNumber2: submitData.r508ParamSetForAdmin.laborConsultantPhoneNumber2,
                      }
                    : {}),
                },
              },
            },
          })
          await updateProcedureStatus({
            variables: {
              input: {
                id: procedureStatusId,
                initialized: true,
              },
            },
          })
          break
        case 'mypage':
          await updateApplicationTmpData({
            variables: {
              input: {
                procedureStatusId,
                type: 'application',
                name: APPLICATION_TMP_DATA_NAME,
                data: submitData,
              },
            },
          })
          await updateProcedureStatus({
            variables: {
              input: {
                id: procedureStatusId,
                dateOn: blankToNull(
                  isParentalLeave(procedureStatus.procedureType)
                    ? procedureStatus.procedureChildcareDatum?.startedOn &&
                        dateFormat(procedureStatus.procedureChildcareDatum.startedOn, 'YYYY/MM/DD')
                    : submitData.childcareDatum.startedOn
                ),
                initialized: true,
              },
            },
          })
          break
        default:
          return procedureContext satisfies never // exhaustiveness check
      }
    } catch (err) {
      dispatch(asyncError(err))
      return
    }

    return await afterSubmit()
  }

  return (
    <div>
      <div className="l-title-wrap">
        <h1 className="m-title-main">{titleMap[procedureStatus.procedureType]}</h1>
        <EmployeeName employee={procedureStatus.employee} />
      </div>
      <Form
        procedureStatus={procedureStatus}
        submitText={submitText}
        onSubmit={onSubmit}
        procedureContext={procedureContext}
        comments={comments}
        renderReject={renderReject}
      />
    </div>
  )
}

export const schema = yup.object({
  childcareDatum: yup.object({
    employeeDependentId: yup.string().notRequired(),
    startedOn: yup.string().notRequired().label('育児休業等開始年月日'),
    expectedFinishedOn: yup.string().notRequired().dateFormat().label('育児休業等終了（予定）年月日'),
    isPapaMamaLeavePlus: yup.boolean().notRequired().label('パパママ育休プラスに該当する'),
    leaveDays: yup
      .string()
      .notRequired()
      .matches(/^\d{1,2}$/, {
        message: ({label}) => `${label}を2桁以内の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label('育児休業等取得日数'),
    workingDays: yup
      .string()
      .notRequired()
      .matches(/^\d{1,2}$/, {
        message: ({label}) => `${label}を2桁以内の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label('就業予定日数'),
    extendedExpectedFinishedOn: yup
      .string()
      .notRequired()
      .dateFormat()
      .label('育児休業等終了（予定）年月日（変更後）')
      .when('$procedureType', {
        is: 'extend_childcare',
        then: (schema) => schema.required(),
      }),
    extendedLeaveDays: yup
      .string()
      .notRequired()
      .matches(/^\d{1,2}$/, {
        message: ({label}) => `${label}を2桁以内の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label('変更後の育児休業等取得日数'),
    finishedOn: yup
      .string()
      .notRequired()
      .dateFormat()
      .label('育児休業等終了年月日')
      .when('$procedureType', {
        is: 'finish_childcare',
        then: (schema) => schema.required(),
      }),
    finalizedLeaveDays: yup
      .string()
      .notRequired()
      .matches(/^\d{1,2}$/, {
        message: ({label}) => `${label}を2桁以内の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label('変更後の育児休業等取得日数'),
  }),
  childcareChildDatum: yup.object({
    firstName: yup.string().notRequired().label('名'),
    lastName: yup.string().notRequired().label('姓'),
    firstNameKana: yup.string().notRequired().label('名（カナ）'),
    lastNameKana: yup.string().notRequired().label('姓（カナ）'),
    birthDate: yup.string().notRequired().label('生年月日'),
    relation: yup.string().notRequired().oneOf(['real', 'other']).label('続柄'),
    parentingStartedDate: yup.string().notRequired().dateFormat().label('養育開始年月日（実子以外）'),
    addSupport: yup.boolean().notRequired().label('社会保険の被扶養者に追加する'),
  }),
  childcareLeaveData: yup
    .array(
      yup
        .object({
          startedOn: yup.string().notRequired().dateFormat().label('育児休業等開始年月日'),
          expectedFinishedOn: yup.string().notRequired().dateFormat().label('育児休業等終了（予定）年月日'),
          leaveDays: yup
            .string()
            .notRequired()
            .numeric()
            .matches(/^\d{1,2}$/, {
              message: ({label}) => `${label}を99日以内で入力してください`,
              excludeEmptyString: true,
            })
            .label('育児休業等取得日数'),
          workingDays: yup
            .string()
            .notRequired()
            .numeric()
            .matches(/^\d{1,2}$/, {
              message: ({label}) => `${label}を99日以内で入力してください`,
              excludeEmptyString: true,
            })
            .label('就業予定日数'),
        })
        .required()
    )
    .required()
    .min(1)
    .max(MAX_LEAVE_DATA_COUNT)
    .label('育休取得内訳')
    .formErrorsMeta({ordinalFormat: '$number'}),
  r508ParamSet: yup.object({
    notes: yup.string().notRequired().max(14).label('備考'),
  }),
  r508ParamSetForAdmin: yup
    .object({
      isUseMyNumber: yup.string().notRequired().oneOf(['true', 'false']).label('マイナンバーを出力する'),
      submitOn: yup.string().notRequired().dateFormat().label('提出年月日'),
    })
    .concat(laborConsultantSchema)
    .nullable()
    .when('$procedureContext', {
      is: 'admin',
      then: (schema) => schema.required(),
    }),
})

export type Schema = yup.InferType<typeof schema>

const makeBlankLeaveDatum = (): Schema['childcareLeaveData'][number] => ({
  startedOn: '',
  expectedFinishedOn: '',
  leaveDays: '',
  workingDays: '',
})

// 表示行数は保存済みの内訳件数に合わせる。
// リリース前の手続きは空欄でも常に4件保存されているため4行になり、リリース後の手続きは保存件数どおりになる
const makeDefaultLeaveData = (
  childcare: R508ReportQueryResult['client']['procedureStatus']['procedureChildcareDatum']
): Schema['childcareLeaveData'] => {
  const leaveDataByOrdinal = keyBy(childcare?.procedureChildcareLeaveData, 'ordinal')
  const ordinals = Object.keys(leaveDataByOrdinal).map(Number)
  const count = Math.min(ordinals.length ? Math.max(...ordinals) + 1 : 1, MAX_LEAVE_DATA_COUNT)

  return range(count).map((_, index) => {
    const leave = leaveDataByOrdinal[index]
    return {
      startedOn: leave?.startedOn ? dateFormat(leave.startedOn, 'YYYY/MM/DD') : '',
      expectedFinishedOn: leave?.expectedFinishedOn ? dateFormat(leave.expectedFinishedOn, 'YYYY/MM/DD') : '',
      leaveDays: String(leave?.leaveDays ?? ''),
      workingDays: String(leave?.workingDays ?? ''),
    }
  })
}

const canInputDays = (startedOn?: string | null, expectedFinishedOn?: string | null) => {
  if (!expectedFinishedOn) {
    return true
  }

  const startedDate = moment(startedOn || undefined, 'YYYY/MM/DD', true)
  const expectedFinishedNextDate = moment(expectedFinishedOn, 'YYYY/MM/DD', true).add(1, 'day')

  return (
    startedDate.isValid() && expectedFinishedNextDate.isValid() && startedDate.isSame(expectedFinishedNextDate, 'month')
  )
}

const hasWorkingDaysField = (index: number) => index < 2

export const normalizeSubmitData = (data: Schema): Schema => {
  // 帳票先頭・育休取得内訳の項目は、育休情報入力画面の開始日/終了予定日による同じ活性条件で判定する
  const canInputDaysValue = canInputDays(data.childcareDatum.startedOn, data.childcareDatum.expectedFinishedOn)

  return {
    ...data,
    childcareDatum: {
      ...data.childcareDatum,
      leaveDays: canInputDaysValue ? data.childcareDatum.leaveDays : '',
      workingDays: canInputDaysValue ? data.childcareDatum.workingDays : '',
    },
    childcareLeaveData: data.childcareLeaveData.map((leave, index) => {
      return {
        ...leave,
        startedOn: canInputDaysValue ? leave.startedOn : '',
        expectedFinishedOn: canInputDaysValue ? leave.expectedFinishedOn : '',
        leaveDays: canInputDaysValue ? leave.leaveDays : '',
        // 3件目以降は入力欄が無いためクリアする。手続き種別による非活性では既存の値を残す（帳票には出力されるため）
        workingDays: canInputDaysValue && hasWorkingDaysField(index) ? leave.workingDays : '',
      }
    }),
  }
}

export const makeDefaultValues = (ps: R508ReportQueryResult['client']['procedureStatus']): Schema => {
  if (ps.application?.data && !ps.r508ParamSet.createdAt) {
    const {application, ...withoutApplication} = ps
    const defaultValues = makeDefaultValues(withoutApplication)
    const applicationValues = omit(schema.validateSync(application.data, {strict: true}), ['r508ParamSetForAdmin'])
    // 内訳は application に保存されている行数（リリース前の手続きは常に4行）のまま復元する
    return {
      ...defaultValues,
      ...applicationValues,
      ...(ps.procedureChildcareDatum
        ? {
            childcareDatum: {
              ...defaultValues.childcareDatum,
              ...applicationValues.childcareDatum,
              employeeDependentId: defaultValues.childcareDatum.employeeDependentId,
              startedOn: defaultValues.childcareDatum.startedOn,
              expectedFinishedOn: defaultValues.childcareDatum.expectedFinishedOn,
            },
            childcareChildDatum: defaultValues.childcareChildDatum,
          }
        : {}),
    }
  }

  const childcare = ps.procedureChildcareDatum
  const child = childcare?.procedureChildcareChildDatum
  const r508 = ps.r508ParamSet
  const r508Admin = ps.r508ParamSetForAdmin

  return {
    childcareDatum: {
      employeeDependentId: childcare?.employeeDependent?.id,
      startedOn: childcare?.startedOn ? dateFormat(childcare.startedOn, 'YYYY/MM/DD') : '',
      expectedFinishedOn: childcare?.expectedFinishedOn ? dateFormat(childcare.expectedFinishedOn, 'YYYY/MM/DD') : '',
      isPapaMamaLeavePlus: childcare?.isPapaMamaLeavePlus || false,
      leaveDays: String(childcare?.leaveDays ?? ''),
      workingDays: String(childcare?.workingDays ?? ''),
      extendedExpectedFinishedOn: childcare?.extendedExpectedFinishedOn
        ? dateFormat(childcare.extendedExpectedFinishedOn, 'YYYY/MM/DD')
        : '',
      extendedLeaveDays: String(childcare?.extendedLeaveDays ?? ''),
      finishedOn: childcare?.finishedOn ? dateFormat(childcare.finishedOn, 'YYYY/MM/DD') : '',
      finalizedLeaveDays: String(childcare?.finalizedLeaveDays ?? ''),
    },
    childcareChildDatum: {
      firstName: child?.firstName,
      lastName: child?.lastName,
      firstNameKana: child?.firstNameKana,
      lastNameKana: child?.lastNameKana,
      birthDate: child?.birthDate ? dateFormat(child.birthDate, 'YYYY/MM/DD') : '',
      relation: child?.relation || 'real',
      parentingStartedDate: child?.parentingStartedDate ? dateFormat(child.parentingStartedDate, 'YYYY/MM/DD') : '',
      addSupport: child?.addSupport || false,
    },
    childcareLeaveData: makeDefaultLeaveData(childcare),
    r508ParamSet: {
      notes: r508.notes,
    },
    r508ParamSetForAdmin: r508Admin
      ? {
          isUseMyNumber: ((v: boolean | undefined) => {
            switch (v) {
              case true:
                return 'true'
              case false:
                return 'false'
              default:
                return undefined
            }
          })(r508Admin.isUseMyNumber),
          submitOn: r508Admin.submitOn ? dateFormat(r508Admin.submitOn, 'YYYY/MM/DD') : '',
          needLaborConsultant: r508Admin.needLaborConsultant,
          laborConsultantCreateDate: r508Admin.laborConsultantCreateDate
            ? dateFormat(r508Admin.laborConsultantCreateDate, 'YYYY/MM/DD')
            : '',
          laborConsultantName: r508Admin.laborConsultantName || '',
          laborConsultantAgentType: r508Admin.laborConsultantAgentType || undefined,
          laborConsultantPrefectureId: r508Admin.laborConsultantPrefecture?.id || undefined,
          laborConsultantPhoneNumber0: r508Admin.laborConsultantPhoneNumber0 || '',
          laborConsultantPhoneNumber1: r508Admin.laborConsultantPhoneNumber1 || '',
          laborConsultantPhoneNumber2: r508Admin.laborConsultantPhoneNumber2 || '',
        }
      : null,
  }
}

interface FormProps {
  procedureStatus: R508ReportQueryResult['client']['procedureStatus']
  submitText: string
  onSubmit: SubmitHandler<Schema>
  procedureContext: ProcedureContext
  comments?: ReactNode
  renderReject?: ReportFormProps['renderReject']
}

const Form: FC<FormProps> = ({procedureStatus, submitText, onSubmit, procedureContext, comments, renderReject}) => {
  const methods = useForm<Schema>({
    defaultValues: makeDefaultValues(procedureStatus),
    resolver: yupResolver(schema),
    mode: 'onTouched',
    shouldFocusError: false,
    context: {procedureContext, procedureType: procedureStatus.procedureType},
  })
  const {
    register,
    handleSubmit,
    control,
    formState: {errors, isSubmitting},
  } = methods

  const startedOn = useWatch({control, name: 'childcareDatum.startedOn'})
  const expectedFinishedOn = useWatch({control, name: 'childcareDatum.expectedFinishedOn'})
  const commonDaysDisabled = !canInputDays(startedOn, expectedFinishedOn)

  const handleSubmitError = () => {
    window.scrollTo({top: 0, behavior: 'smooth'})
  }
  const {confirmUpgrade, confirmUpgradeModal} = useConfirmUpgrade(
    procedureStatus.r508ParamSet.needUpgrade,
    handleSubmit(onSubmit, handleSubmitError)
  )

  return (
    <FormProvider {...methods}>
      <div className="basic_information l-wrap-xs">
        <FormErrors errors={errors} isSubmitting={isSubmitting} schema={schema} prependFullPath />
        {comments}

        <Panel className="u-mt20">
          <Head>健康保険・厚生年金保険育児休業等取得者申出書（新規・延長）/終了届</Head>
          <Body>
            <div className={styles.formBody}>
              <Note>
                「育児休業等開始年月日」と「育児休業等終了（予定）年月日の翌日」が同月内の場合のみ入力可能になります。
              </Note>
              <div className={styles.field}>
                <Label>育児休業等取得日数</Label>
                <InputWithText
                  {...register('childcareDatum.leaveDays')}
                  text="日"
                  disabled={commonDaysDisabled}
                  isError={!!errors.childcareDatum?.leaveDays}
                />
                {errors.childcareDatum?.leaveDays?.message && (
                  <ErrorMessage>{errors.childcareDatum.leaveDays.message}</ErrorMessage>
                )}
              </div>
              <div className={styles.field}>
                <Label>就業予定日数</Label>
                <InputWithText
                  {...register('childcareDatum.workingDays')}
                  text="日"
                  disabled={commonDaysDisabled}
                  isError={!!errors.childcareDatum?.workingDays}
                />
                {errors.childcareDatum?.workingDays?.message && (
                  <ErrorMessage>{errors.childcareDatum.workingDays.message}</ErrorMessage>
                )}
              </div>
            </div>

            {procedureStatus.procedureType === 'extend_childcare' && (
              <Panel className="u-mt20">
                <Head>終了予定日を延長する場合</Head>
                <Body className={styles.formBody}>
                  <DateField
                    label="育児休業等終了（予定）年月日（変更後）"
                    required
                    name="childcareDatum.extendedExpectedFinishedOn"
                    error={errors.childcareDatum?.extendedExpectedFinishedOn?.message}
                  />
                  <div className={styles.field}>
                    <Label>変更後の育児休業等取得日数</Label>
                    <InputWithText
                      {...register('childcareDatum.extendedLeaveDays')}
                      text="日"
                      isError={!!errors.childcareDatum?.extendedLeaveDays}
                    />
                    {errors.childcareDatum?.extendedLeaveDays?.message && (
                      <ErrorMessage>{errors.childcareDatum.extendedLeaveDays.message}</ErrorMessage>
                    )}
                    <Note>
                      ※延長後の「育児休業等終了（予定）年月日の翌日」が「育児休業開始年月日」と同月内の場合は、変更後の育児休業等取得日数欄も記入してください
                    </Note>
                  </div>
                </Body>
              </Panel>
            )}

            {procedureStatus.procedureType === 'finish_childcare' && (
              <Panel className="u-mt20">
                <Head>予定より早く育児休業を終了した場合</Head>
                <Body className={styles.formBody}>
                  <DateField
                    label="育児休業等終了年月日"
                    required
                    name="childcareDatum.finishedOn"
                    error={errors.childcareDatum?.finishedOn?.message}
                  />
                  <div className={styles.field}>
                    <Label>変更後の育児休業等取得日数</Label>
                    <InputWithText
                      {...register('childcareDatum.finalizedLeaveDays')}
                      text="日"
                      isError={!!errors.childcareDatum?.finalizedLeaveDays}
                    />
                    {errors.childcareDatum?.finalizedLeaveDays?.message && (
                      <ErrorMessage>{errors.childcareDatum.finalizedLeaveDays.message}</ErrorMessage>
                    )}
                    <Note>
                      ※「育児休業等終了年月日の翌日」が「育児休業開始年月日」と同月内の場合は、変更後の育児休業等取得日数欄も記入してください
                    </Note>
                  </div>
                </Body>
              </Panel>
            )}

            <LeaveDataSection register={register} control={control} procedureType={procedureStatus.procedureType} />

            <div className={classNames(styles.field, 'u-mt20')}>
              <Label>パパママ育休プラス該当区分</Label>
              <Checkbox {...register('childcareDatum.isPapaMamaLeavePlus')}>パパママ育休プラスに該当する</Checkbox>
            </div>
            <div className={classNames(styles.field, 'u-mt20')}>
              <Label>備考</Label>
              <Textarea {...register('r508ParamSet.notes')} isError={!!errors.r508ParamSet?.notes} />
              {errors.r508ParamSet?.notes?.message && <ErrorMessage>{errors.r508ParamSet.notes.message}</ErrorMessage>}
            </div>
            {procedureContext === 'admin' && (
              <div className={classNames(styles.field, 'u-mt20')}>
                <Label>本人確認番号</Label>
                <RadioContainer>
                  <Radio
                    {...register('r508ParamSetForAdmin.isUseMyNumber')}
                    value="true"
                    disabled={!procedureStatus.employee.myNumberPresent}
                  >
                    マイナンバーを出力する
                  </Radio>
                  <Radio {...register('r508ParamSetForAdmin.isUseMyNumber')} value="false">
                    基礎年金番号を出力する
                  </Radio>
                </RadioContainer>
                {!procedureStatus.employee.myNumberPresent && (
                  <Note>
                    マイナンバーを記入する場合は、マイナンバーを入力した後に帳票の編集画面から再度選択してください。
                  </Note>
                )}
              </div>
            )}

            {procedureContext === 'admin' && (
              <>
                <div className={classNames(styles.field, 'u-mt20')}>
                  <Label>提出年月日</Label>
                  <DateField
                    name="r508ParamSetForAdmin.submitOn"
                    error={errors.r508ParamSetForAdmin?.submitOn?.message}
                  />
                </div>

                <LaborConsultantSection
                  className="u-mt20"
                  procedureStatusId={procedureStatus.id}
                  reportName="R508"
                  prefix="r508ParamSetForAdmin."
                />
              </>
            )}
          </Body>
        </Panel>

        <div className="u-ta-c u-mt50">
          {renderReject?.({isSubmitting})}
          <Button primary onClick={confirmUpgrade} disabled={isSubmitting}>
            {submitText}
          </Button>
        </div>
      </div>

      {confirmUpgradeModal}
    </FormProvider>
  )
}

interface LeaveDataSectionProps {
  register: UseFormRegister<Schema>
  control: Control<Schema>
  procedureType: string
}

const LeaveDataSection: FC<LeaveDataSectionProps> = ({register, control, procedureType}) => {
  const {errors} = useFormState({control})
  const {fields, append, remove} = useFieldArray({control, name: 'childcareLeaveData'})
  const startedOn = useWatch({control, name: 'childcareDatum.startedOn'})
  const expectedFinishedOn = useWatch({control, name: 'childcareDatum.expectedFinishedOn'})
  const daysDisabled = !canInputDays(startedOn, expectedFinishedOn)
  // 就業予定日数は出生時育休だけの項目
  const workingDaysDisabled = daysDisabled || !isParentalLeave(procedureType)

  const handleAdd = () => {
    if (fields.length < MAX_LEAVE_DATA_COUNT) {
      append(makeBlankLeaveDatum(), {shouldFocus: false})
    }
  }

  return (
    <>
      {fields.map((field, index) => {
        const err = errors.childcareLeaveData?.[index]
        const showWorkingDays = hasWorkingDaysField(index)

        return (
          <div key={field.id} className="u-mt20" data-section={`育休取得内訳${index + 1}`}>
            <Panel>
              <Head>
                <span>育休取得内訳{index + 1}</span>
                {index > 0 && (
                  <span data-action="delete" title="育休取得内訳を削除">
                    <ActionButton icon={<Delete size={16} />} onClick={() => remove(index)} />
                  </span>
                )}
              </Head>
              <Body className={styles.formBody}>
                <p>
                  「育児休業等開始年月日」と「育児休業等終了（予定）年月日の翌日」が同月内、かつ複数回育児休業取得する場合は入力してください。
                </p>
                <div className={styles.field}>
                  <Label>育児休業等開始年月日</Label>
                  <DateField
                    name={`childcareLeaveData.${index}.startedOn`}
                    disabled={daysDisabled}
                    error={err?.startedOn?.message}
                  />
                </div>
                <div className={styles.field}>
                  <Label>育児休業等終了（予定）年月日</Label>
                  <DateField
                    name={`childcareLeaveData.${index}.expectedFinishedOn`}
                    disabled={daysDisabled}
                    error={err?.expectedFinishedOn?.message}
                  />
                </div>
                <div className={styles.field}>
                  <Label>育児休業等取得日数</Label>
                  <InputWithText
                    {...register(`childcareLeaveData.${index}.leaveDays`)}
                    text="日"
                    disabled={daysDisabled}
                    isError={!!err?.leaveDays}
                  />
                  {err?.leaveDays?.message && <ErrorMessage>{err.leaveDays.message}</ErrorMessage>}
                </div>
                {showWorkingDays && (
                  <div className={styles.field}>
                    <Label>就業予定日数</Label>
                    <InputWithText
                      {...register(`childcareLeaveData.${index}.workingDays`)}
                      text="日"
                      disabled={workingDaysDisabled}
                      isError={!!err?.workingDays}
                    />
                    {err?.workingDays?.message && <ErrorMessage>{err.workingDays.message}</ErrorMessage>}
                  </div>
                )}
              </Body>
            </Panel>
          </div>
        )
      })}
      {fields.length < MAX_LEAVE_DATA_COUNT && (
        <div className="u-ta-r u-mt20">
          <div onClick={handleAdd} className="u-txt-addinfo">
            <PlusSquare size={20} className="u-txt-addinfo-icon" />
            育休取得内訳追加
          </div>
        </div>
      )}
    </>
  )
}
